/* eslint-disable jsx-a11y/anchor-is-valid */
import { Button, Card, Dropdown, LinkButton } from '@pmi/dsm-react';
import { Form, Spinner } from 'react-bootstrap';
import { Option } from '@pmi/dsm-react/dist/components/menus/types';
import { Formik } from 'formik';
import { useSelector } from 'react-redux';
import { accountSelector } from '../../../shared/store/shared/accountSlice';
import { useGetAccountDataQuery } from '../../../shared/navigation/apiSlice';
import { useUpdateAccount2faMutation } from './apiSlice';
import { toastWithAnalytics } from '../../../shared/analytics';
import styles from './twofaCadence.module.scss';
import usePermissions from '../../../shared/hooks/usePermissions';
import PERMISSIONS from '../../../shared/permissions';

const twoFaOptions: Option[] = [
  {
    value: (24 * 60).toString(),
    label: 'Daily',
  },
  {
    value: (7 * 24 * 60).toString(),
    label: 'Weekly',
  },
  {
    value: (14 * 24 * 60).toString(),
    label: 'Bi-weekly',
  },
  {
    value: (30 * 24 * 60).toString(),
    label: 'Monthly',
  },
];

const default2faInterval = (24 * 60).toString();

export default function TwofaCadence() {
  const { currentAccountId } = useSelector(accountSelector);
  const {
    data: accountData,
    isLoading,
    isFetching,
  } = useGetAccountDataQuery(currentAccountId, { skip: !currentAccountId });
  const [updateAccount2fa] = useUpdateAccount2faMutation();
  const { hasAccess } = usePermissions();

  const initialValues = {
    twoFaInterval: !accountData?.twoFaInterval
      ? default2faInterval
      : accountData.twoFaInterval.toString(),
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
          await updateAccount2fa({
            accountId: currentAccountId,
            twoFaInterval: Number(values.twoFaInterval),
          }).unwrap();

          toastWithAnalytics(
            `Two-factor authentication login prompt frequency updated to ${
              twoFaOptions.find((e) => e.value === values.twoFaInterval)?.label
            }`,
            'success',
          );
        } catch {
          toastWithAnalytics(
            'Error while updating Two-factor authentication login prompt frequency',
            'danger',
          );
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {(form) => (
        <Card className="p-0">
          <div className="p-4">
            <div className="row">
              <div className="col">
                <div className="d-flex flex-column">
                  <div className="flex-fill">
                    <h4 className="fw-bold mb-0">
                      User Two-Factor Authentication
                    </h4>
                  </div>

                  <div className="row gx-2 align-items-center">
                    <span className={styles.twoFaDesc}>
                      Two-factor authentication adds an additional layer of
                      security to your Agreements. Accounts that require 2FA
                      frequently are more likely to safeguard their products
                      from malicious enrollments from former employees or shared
                      accounts that result in charges to your invoice.
                    </span>
                  </div>

                  {(isLoading || isFetching) && (
                    <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                      <Spinner />
                    </div>
                  )}

                  {!isLoading && !isFetching && (
                    <div style={{ maxWidth: '450px' }} className="mt-3">
                      <Form>
                        <Form.Group controlId="twoFa">
                          <Form.Label>
                            Two-factor authentication login prompt frequency
                            (for Users only):
                          </Form.Label>
                          <Dropdown
                            options={twoFaOptions}
                            value={form.values.twoFaInterval}
                            disabled={
                              !hasAccess({
                                permissions: [PERMISSIONS.UpdateAccount2fa],
                              })
                            }
                            onChange={(_, value) => {
                              form.setFieldValue('twoFaInterval', value);
                            }}
                            onChangeInput={(e) => {
                              e.bubbles = false;
                            }}
                            placeholder=""
                          />
                        </Form.Group>
                      </Form>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-auto">
                <div
                  className={`d-flex flex-wrap ${
                    form.dirty ? 'visible' : 'invisible'
                  }`}
                >
                  {hasAccess({
                    permissions: [PERMISSIONS.UpdateAccount2fa],
                  }) && (
                    <>
                      <LinkButton
                        titleText="Cancel Changes"
                        className={`me-2 ${styles.cancelbtn}`}
                        size="sm"
                        onClick={form.resetForm}
                        disabled={form.isSubmitting}
                      />
                      <Button
                        titleText="Save Changes"
                        variant="primary"
                        onClick={form.submitForm}
                        disabled={form.isSubmitting}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </Formik>
  );
}
